import React, { useState } from 'react';
import { ChevronDown, Loader2, Pencil, Star, Trash2 } from 'lucide-react';
import { api, useAuth } from '../context/AuthContext';

export const KRITERIEN = [['fachlich', 'Fachlich'], ['auftreten', 'Auftreten'], ['motivation', 'Motivation']];
const fmt = (iso) => { try { return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-wider rounded-sm transition-colors duration-200 disabled:opacity-50';

export const schnitt = (notizen = []) => {
  const w = notizen.flatMap((n) => KRITERIEN.map(([k]) => n[k]).filter(Boolean));
  return w.length ? Math.round((w.reduce((a, b) => a + b, 0) / w.length) * 10) / 10 : null;
};

export const Sterne = ({ wert, onChange, testId, klein }) => (
  <span className="inline-flex items-center" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((i) => (
      <button key={i} type="button" disabled={!onChange} onClick={() => onChange?.(wert === i ? null : i)} className={onChange ? 'p-0.5 transition-transform hover:scale-110' : 'p-px cursor-default'} data-testid={testId ? `${testId}-${i}` : undefined} aria-label={`${i} Sterne`}>
        <Star className={`${klein ? 'w-3 h-3' : 'w-4 h-4'} ${wert && i <= Math.round(wert) ? 'fill-[#b8893d] text-[#b8893d]' : 'text-gray-300'}`} />
      </button>
    ))}
  </span>
);

const leer = { fachlich: null, auftreten: null, motivation: null, text: '' };

const NotizForm = ({ start, busy, onSave, onCancel, id }) => {
  const [f, setF] = useState(start || leer);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="bg-[#fbf7f0] border border-[#b8893d]/20 rounded-sm p-3 space-y-2" data-testid={`notiz-form-${id}`}>
      <div className="flex flex-wrap gap-x-6 gap-y-1.5">
        {KRITERIEN.map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-xs text-[#34536b]"><span className="w-20">{l}</span><Sterne wert={f[k]} onChange={(v) => setF((s) => ({ ...s, [k]: v }))} testId={`notiz-${k}-${id}`} /></label>
        ))}
      </div>
      <textarea rows={3} maxLength={5000} value={f.text} onChange={(e) => setF((s) => ({ ...s, text: e.target.value }))} placeholder="Eindrücke aus dem Gespräch …" className="w-full border border-gray-300 rounded-sm p-2 text-sm bg-white" data-testid={`notiz-text-${id}`} />
      <div className="flex justify-end gap-2">
        {onCancel && <button type="button" onClick={onCancel} className={`${btn} border border-gray-300 text-[#1B3A5C]`} data-testid={`notiz-abbrechen-${id}`}>ABBRECHEN</button>}
        <button type="submit" disabled={busy || (!f.text.trim() && !KRITERIEN.some(([k]) => f[k]))} className={`${btn} bg-[#1B3A5C] text-white hover:bg-[#b8893d]`} data-testid={`notiz-speichern-${id}`}>{busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} SPEICHERN</button>
      </div>
    </form>
  );
};

const Notiz = ({ n, darf, busy, onEdit, onDel }) => (
  <li className="py-2.5" data-testid={`notiz-${n.id}`}>
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      <span className="text-xs font-semibold text-[#1B3A5C]">{n.autor}</span>
      <span className="text-[10px] text-gray-500">{fmt(n.am)}{n.geaendert_am ? ' · bearbeitet' : ''}</span>
      {KRITERIEN.map(([k, l]) => n[k] ? <span key={k} className="inline-flex items-center gap-1 text-[10px] text-[#34536b]">{l} <Sterne wert={n[k]} klein /></span> : null)}
      {darf && <span className="ml-auto flex gap-1">
        <button type="button" onClick={onEdit} disabled={busy} className="p-1 text-[#34536b] hover:text-[#b8893d]" data-testid={`notiz-bearbeiten-${n.id}`} aria-label="Bearbeiten"><Pencil className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={onDel} disabled={busy} className="p-1 text-[#7a8a99] hover:text-rose-700" data-testid={`notiz-loeschen-${n.id}`} aria-label="Löschen"><Trash2 className="w-3.5 h-3.5" /></button>
      </span>}
    </div>
    {n.text && <p className="text-sm text-[#34536b] whitespace-pre-wrap mt-1">{n.text}</p>}
  </li>
);

export const BewerbungNotizen = ({ it, toast, onDone }) => {
  const { user } = useAuth();
  const [offen, setOffen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const notizen = it.notizen || [];
  const avg = schnitt(notizen);
  const base = `/admin/anfragen/bewerbungen/${it.bewerbung_id}/notizen`;
  const run = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); onDone(data); setEdit(null); toast({ title: ok }); }
    catch (e) { toast({ title: 'Fehler', description: e?.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`notizen-${it.bewerbung_id}`}>
      <button type="button" onClick={() => setOffen((x) => !x)} className="inline-flex items-center gap-2 text-xs tracking-wider text-[#34536b] hover:text-[#b8893d]" data-testid={`notizen-toggle-${it.bewerbung_id}`}>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${offen ? 'rotate-180' : ''}`} /> GESPRÄCHSNOTIZEN (NUR TEAM) · {notizen.length}
        {avg && <span className="inline-flex items-center gap-1 normal-case tracking-normal" data-testid={`notizen-schnitt-${it.bewerbung_id}`}><Sterne wert={avg} klein /> Ø {avg.toLocaleString('de-DE')}</span>}
      </button>
      {offen && (
        <div className="mt-2 space-y-2">
          {notizen.length > 0 && <ul className="divide-y divide-gray-100">{notizen.map((n) => (edit === n.id
            ? <li key={n.id} className="py-2"><NotizForm id={n.id} start={n} busy={busy} onCancel={() => setEdit(null)} onSave={(f) => run(() => api.patch(`${base}/${n.id}`, f), 'Notiz aktualisiert')} /></li>
            : <Notiz key={n.id} n={n} busy={busy} darf={n.user_id === user?.user_id || user?.role === 'admin'} onEdit={() => setEdit(n.id)} onDel={() => window.confirm('Notiz löschen?') && run(() => api.delete(`${base}/${n.id}`), 'Notiz gelöscht')} />))}</ul>}
          {!edit && <NotizForm key={notizen.length} id={`neu-${it.bewerbung_id}`} busy={busy} onSave={(f) => run(() => api.post(base, f), 'Notiz gespeichert')} />}
        </div>
      )}
    </div>
  );
};
